import React from 'react';
import { Card, CardContent, Box, Skeleton, Stack } from '@mui/material';

interface QuestionSkeletonProps {
  variant?: 'card' | 'inline' | 'compact';
  showDragHandle?: boolean;
  animate?: boolean;
}

export const QuestionSkeleton: React.FC<QuestionSkeletonProps> = ({
  variant = 'card',
  showDragHandle = true,
  animate = true,
}) => {
  if (variant === 'inline') {
    return (
      <Stack spacing={1} sx={{ py: 1 }}>
        <Skeleton variant="text" width="20%" height={20} animation={animate ? 'wave' : false} />
        <Skeleton variant="text" width="80%" height={32} animation={animate ? 'wave' : false} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={60}
          animation={animate ? 'wave' : false}
        />
      </Stack>
    );
  }

  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
        <Skeleton variant="circular" width={24} height={24} animation={animate ? 'wave' : false} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} animation={animate ? 'wave' : false} />
        </Box>
        <Skeleton
          variant="rectangular"
          width={60}
          height={24}
          animation={animate ? 'wave' : false}
        />
      </Box>
    );
  }

  // Default card variant
  return (
    <Card
      sx={{
        mb: 2,
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Drag Handle Skeleton */}
      {showDragHandle && (
        <Box
          sx={{
            position: 'absolute',
            left: -2,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'grey.200',
            borderRadius: '0 4px 4px 0',
            p: 0.5,
          }}
        >
          <Skeleton
            variant="rectangular"
            width={16}
            height={16}
            animation={animate ? 'wave' : false}
          />
        </Box>
      )}

      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton
              variant="rectangular"
              width={32}
              height={20}
              sx={{ borderRadius: 1 }}
              animation={animate ? 'wave' : false}
            />
            <Skeleton variant="text" width={80} height={20} animation={animate ? 'wave' : false} />
            <Skeleton
              variant="rectangular"
              width={60}
              height={20}
              sx={{ borderRadius: 1 }}
              animation={animate ? 'wave' : false}
            />
          </Stack>

          <Stack direction="row" spacing={1}>
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              animation={animate ? 'wave' : false}
            />
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              animation={animate ? 'wave' : false}
            />
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              animation={animate ? 'wave' : false}
            />
          </Stack>
        </Box>

        {/* Title */}
        <Skeleton
          variant="text"
          width="75%"
          height={32}
          sx={{ mb: 1 }}
          animation={animate ? 'wave' : false}
        />

        {/* Description */}
        <Skeleton
          variant="text"
          width="90%"
          height={20}
          sx={{ mb: 2 }}
          animation={animate ? 'wave' : false}
        />

        {/* Content Area */}
        <Box sx={{ mt: 2 }}>
          <Stack spacing={1}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={40}
              sx={{ borderRadius: 1 }}
              animation={animate ? 'wave' : false}
            />
            <Skeleton
              variant="rectangular"
              width="80%"
              height={40}
              sx={{ borderRadius: 1 }}
              animation={animate ? 'wave' : false}
            />
            <Skeleton
              variant="rectangular"
              width="60%"
              height={40}
              sx={{ borderRadius: 1 }}
              animation={animate ? 'wave' : false}
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

interface QuestionListSkeletonProps {
  count?: number;
  variant?: 'card' | 'inline' | 'compact';
  showDragHandle?: boolean;
  staggered?: boolean;
}

export const QuestionListSkeleton: React.FC<QuestionListSkeletonProps> = ({
  count = 3,
  variant = 'card',
  showDragHandle = true,
  staggered = true,
}) => {
  return (
    <Stack spacing={variant === 'card' ? 3 : 2}>
      {Array.from({ length: count }, (_, index) => (
        <Box
          key={index}
          sx={{
            opacity: staggered ? Math.max(0.3, 1 - index * 0.2) : 1,
            animationDelay: staggered ? `${index * 100}ms` : '0ms',
          }}
        >
          <QuestionSkeleton
            variant={variant}
            showDragHandle={showDragHandle}
            animate={index < 2} // Only animate first 2 for performance
          />
        </Box>
      ))}
    </Stack>
  );
};
